import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import {
  DELETE_ROUTINE_DRAFT,
  FIND_ROUTINE_DRAFT,
  Severity,
  useCustomMutation,
  useCustomQuery,
  WorkspaceAsset,
} from '@gripp/shared-logic';
import {
  Colors,
  EditRoutineModal,
  Layout,
  RoutineButtons,
  RoutineDraftModal,
  RoutineExecutionProps,
  RoutineModal,
  SeverityIcons,
  TopModalBar,
  TouchableButton,
  popNextAutoPrompt,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';

export type RoutinesAssignedListModalProps = {
  workspaceAsset: WorkspaceAsset;
  isOpened: boolean;
  onCompleteRoutines: () => Promise<void>;
  onCancel?: () => void;
  initialRoutine?: RoutineExecutionProps;
};

export const RoutinesAssignedListModal = (
  props: RoutinesAssignedListModalProps
) => {
  const [routinesModalStyle, setRoutinesModalStyle] = useState<
    StyleProp<ViewStyle>
  >(styles.outerContainer);
  const [editModalStyle, setEditModalStyle] = useState<StyleProp<ViewStyle>>(
    styles.hidden
  );
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [executeModalVisible, setExecuteModalVisible] = useState(false);
  const [executeModalProps, setExecuteModalProps] = useState<
    RoutineExecutionProps | undefined
  >();
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [loadedDraftData, setLoadedDraftData] = useState<any>(null);
  const [formKey, setFormKey] = useState(0);
  const [justSavedDraft, setJustSavedDraft] = useState(false);

  useEffect(() => {
    setRoutinesModalStyle(styles.outerContainer);
  }, [props.isOpened]);

  // Handle initial routine (auto-prompt)
  useEffect(() => {
    if (props.isOpened && props.initialRoutine) {
      setExecuteModalProps(props.initialRoutine);
      setJustSavedDraft(false);
      setRoutinesModalStyle(styles.hidden); // Hide routines list
      setExecuteModalVisible(true);
    }
  }, [props.isOpened, props.initialRoutine]);

  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Query to check for existing drafts
  const { data: draftQueryData, isLoading: isDraftLoading } = useCustomQuery({
    cacheKey: [
      'routineDraft',
      props.workspaceAsset?.id,
      executeModalProps?.routine?.id,
    ],
    query: FIND_ROUTINE_DRAFT,
    variables: {
      workspaceAssetId: props.workspaceAsset?.id || '',
      routineId: executeModalProps?.routine?.id || '',
    },
    options: {
      enabled: !!(
        props.workspaceAsset?.id &&
        executeModalProps?.routine?.id &&
        executeModalVisible
      ),
    },
  });

  const draft = draftQueryData?.routine?.findRoutineDraft;

  // Mutation to delete drafts
  const { mutateAsync: deleteDraftAsync } = useCustomMutation({
    query: DELETE_ROUTINE_DRAFT,
  });

  useEffect(() => {
    if (!isDraftLoading && executeModalProps && executeModalVisible) {
      if (draft && !justSavedDraft) {
        // Show draft modal on top
        setShowDraftModal(true);
      } else {
        // No draft
        setLoadedDraftData(null);
      }
    }
  }, [
    draft,
    isDraftLoading,
    justSavedDraft,
    executeModalProps,
    executeModalVisible,
  ]);

  const icon = (
    <MaterialCommunityIcons
      name={SeverityIcons[Severity.Blue]}
      color={Colors.blue}
      size={24}
      style={{ paddingRight: 6 }}
    />
  );

  const showRoutinesModal = () => {
    setRoutinesModalStyle(styles.outerContainer);
  };

  const hideRoutinesModal = () => {
    setRoutinesModalStyle(styles.hidden);
  };

  const showEditModal = () => {
    setEditModalVisible(true);
    setEditModalStyle(styles.outerContainer);
  };

  const hideEditModal = () => {
    setEditModalVisible(false);
    setEditModalStyle(styles.hidden);
  };

  const onEdit = () => {
    hideRoutinesModal();
    showEditModal();
  };

  const onRoutineSelected = (selectedProps: RoutineExecutionProps) => {
    setExecuteModalProps(selectedProps);
    setJustSavedDraft(false);
    hideRoutinesModal();
    setExecuteModalVisible(true);
  };

  // Draft handlers
  const handleSaveDraft = async () => {
    setJustSavedDraft(true);
    await queryClient.invalidateQueries({
      queryKey: [
        'routineDraft',
        props.workspaceAsset?.id,
        executeModalProps?.routine?.id,
      ],
    });
  };

  const handleContinueDraft = async () => {
    setShowDraftModal(false);
    setLoadedDraftData(draft?.data || null);
    setFormKey((prev) => prev + 1);
  };

  const handleStartNew = async () => {
    setShowDraftModal(false);
    try {
      if (props.workspaceAsset?.id && executeModalProps?.routine?.id) {
        await deleteDraftAsync({
          input: {
            workspaceAssetId: props.workspaceAsset.id,
            routineId: executeModalProps.routine.id,
          },
        });
      }
      setLoadedDraftData(null);
      await queryClient.invalidateQueries({
        queryKey: [
          'routineDraft',
          props.workspaceAsset?.id,
          executeModalProps?.routine?.id,
        ],
      });
      await new Promise((resolve) => setTimeout(resolve, 100));
      setFormKey((prev) => prev + 1);
    } catch (error) {
      console.error('Error deleting draft:', error);
    }
  };

  const handleDraftCancel = () => {
    setShowDraftModal(false);
    setExecuteModalVisible(false);
    setExecuteModalProps(undefined); // Clear the selected routine
    showRoutinesModal();
  };

  const handleRoutineCompleted = async () => {
    try {
      // Delete draft after successful completion
      if (props.workspaceAsset?.id && executeModalProps?.routine?.id) {
        await deleteDraftAsync({
          input: {
            workspaceAssetId: props.workspaceAsset.id,
            routineId: executeModalProps.routine.id,
          },
        });
        await queryClient.invalidateQueries({
          queryKey: [
            'routineDraft',
            props.workspaceAsset?.id,
            executeModalProps?.routine?.id,
          ],
        });
      }
    } catch (error) {
      console.error('Error deleting draft after completion:', error);
    }

    // Check for next auto-prompt routine
    const nextRoutine = executeModalProps
      ? popNextAutoPrompt(executeModalProps)
      : null;
    if (nextRoutine) {
      setExecuteModalProps(nextRoutine);
      setJustSavedDraft(false);
      setLoadedDraftData(null);
      setFormKey((prev) => prev + 1);
      return;
    }

    setExecuteModalVisible(false);
    await props.onCompleteRoutines();
  };

  return (
    <>
      <Modal
        transparent
        visible={props.isOpened}
        onRequestClose={props.onCancel}
      >
        <View style={routinesModalStyle}>
          <View style={styles.innerContainer}>
            <View style={styles.container}>
              <TopModalBar
                title={t('asset.header.buttons.routines')}
                onCancel={props.onCancel}
                additionalHeaderBlock={icon}
                right={<EditButton onPress={onEdit} />}
              />
              <RoutineButtons
                workspaceAsset={props.workspaceAsset}
                onRoutineSelected={onRoutineSelected}
                hideAssignRoutinesButton={true}
                buttonIcon={Severity.Blue}
                buttonIconStyle={styles.buttonIconStyle}
                buttonStyle={styles.buttonStyle}
                buttonsContainerStyle={styles.buttonContainerStyle}
                showAll={true}
                initialRoutinesToShow={10}
              />
            </View>
          </View>
        </View>
      </Modal>
      <EditRoutineModal
        workspaceAsset={props.workspaceAsset}
        editRoutineModalVisible={editModalVisible}
        onClose={() => {
          hideEditModal();
          showRoutinesModal();
        }}
        modalStyle={editModalStyle}
        isWeb={Platform.OS === 'web'}
      />
      {executeModalProps && (
        <RoutineModal
          key={formKey}
          {...executeModalProps}
          visible={executeModalVisible}
          confirmCancel={true}
          onCanceled={() => {
            setExecuteModalVisible(false);
            showRoutinesModal();
          }}
          onCompleted={handleRoutineCompleted}
          onSaveDraft={handleSaveDraft}
          alertsRightButton={true}
          showSaveDraftButton={true}
          draftData={loadedDraftData}
          customModalDraft={
            showDraftModal ? (
              <RoutineDraftModal
                visible={showDraftModal}
                onClose={handleDraftCancel}
                onContinueDraft={handleContinueDraft}
                onStartNew={handleStartNew}
                draft={draft}
                workspaceAsset={props.workspaceAsset}
                routine={executeModalProps?.routine}
                showPortal={false}
              />
            ) : null
          }
          showNativeModal={true}
          showConfirmBack={true}
        />
      )}
    </>
  );
};

const EditButton = ({ onPress }: { onPress: () => void }) => {
  const { t } = useTranslation();
  return (
    <TouchableButton
      label={t('routines.editRoutines.title')}
      style={styles.editButton}
      onPress={onPress}
    />
  );
};

const styles = StyleSheet.create({
  container: {
    ...Layout.container,
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 12,
    paddingBottom: 12,
  },
  innerContainer: {
    width: '100%',
    maxWidth: 560,
    height: 560,
  },
  hidden: {
    height: 0,
    width: 0,
    maxHeight: 0,
    minHeight: 0,
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  outerContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 40,
    backgroundColor: Colors.blackInactive,
  },
  buttonIconStyle: {
    color: Colors.white,
    backgroundColor: Colors.blue,
    borderRadius: 4,
    height: 25,
    width: 25,
    marginBottom: 0,
    marginRight: 12,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
  },
  buttonStyle: {
    backgroundColor: Colors.white,
    borderRadius: 0,
    borderWidth: 0,
    height: 64,
    paddingHorizontal: 20,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: Colors.grayDivider,
  },
  buttonContainerStyle: {
    flex: 0,
    flexGrow: 0,
    minHeight: 461,
    backgroundColor: Colors.white,
  },
  editButton: {
    borderColor: Colors.primaryDark,
    borderWidth: 2,
    borderRadius: 12,
    height: 40,
    justifyContent: 'center',
    paddingHorizontal: 24,
    marginRight: 12,
  },
});
